import React, { useState, useEffect, useRef } from 'react';
import './styles/design-system.css';
import './App.css';

// Import Academic Calendar Module
import AcademicCalendarModule from './components/Calendar/AcademicCalendarModule';

// Import Teacher Directory Module
import TeacherDirectoryModule from './components/TeacherDirectoryModule/TeacherDirectoryModule';

// Import Main Page Module
import MainPage from './components/Core/MainPage';

// Import Attendance Page Module
import AttendancePage from './components/Attendance/AttendancePage';

// Import School Event Page Module
import SchoolEventPage from './components/School/SchoolEventPage';

// Import Grades Management Page Module
import GradesManagementPage from './components/Grades/GradesManagementPage';

// Import Payment Records Module
import PaymentRecordsModule from './components/Payment/PaymentRecordsModule';

// Import News and Notification Module
import NewsAndNotificationModule from './components/NewsAndNotification/NewsAndNotificationModule';

// Import SLP Management Page
import SLPManagementPage from './components/SLP/SLPManagementPage';

// Import SLP Event Page
import SLPEventPage from './components/SLP/SLPEventPage';

// Import SLP Event Details
import SLPEventDetails from './components/SLP/SLPEventDetails';

// Import SLP Event Form
import SLPEventForm from './components/SLP/SLPEventForm';

// Import SLP Student Selector
import SLPStudentSelector from './components/SLP/SLPStudentSelector';

// Class Attendance Detail removed - teachers use AttendancePage instead

// Import School Info Page
import SchoolInfoPage from './components/School/SchoolInfoPage';

// Import Supabase Auth
import ModernLoginForm from './components/Core/ModernLoginForm';

// Import Credits Panel
import CreditsPanel from './components/Core/CreditsPanel';

// Import Auth Sim
import AuthSim from './components/Core/AuthSim';

// Import ECA Page
import ECAPage from './components/ECA/ECAPage';

// Import new Information Pages
import TeacherInfoPage from './components/TeacherInfo';
import StudentInformationDashboard from './components/StudentInfo/StudentInformationDashboard';
import ClassInfoPage from './components/ClassInfo';
import StaffPage from './components/Staff/StaffPage';

// Import Group Messages Module
import GroupMessagesModule from './components/GroupMessages/GroupMessagesModule';

// Import Sports Days Module
import SportsDaysModule from './components/SportsDays/SportsDaysModule';

// Import Timetable Module
import TimetableModule from './components/Timetable/TimetableModule';

// Import Class Management Module
import ClassManagementModule from './components/ClassManagement/ClassManagementModule';

// Import Student Management Module
import StudentManagementModule from './components/StudentManagement/StudentManagementModule';

// Import MyClass Timetable Planner Module
import MyClassTimetablePlanner from './components/TimetablePlanner/MyClassTimetablePlanner';

// Import Lunchbox Module
import LunchboxModule from './components/Lunchbox/LunchboxModule';

// Import Lesson Plan Module
import LessonPlanModule from './components/LessonPlan/LessonPlanModule';

// Import Homework Module
import HomeworkModule from './components/Homework/HomeworkModule';

// Import SLP Module
import SlpModule from './components/SLP/SlpModule';

// Import SDR Module
import SdrModule from './components/SDR/SdrModule';

// Import eNotice Module
import ENoticeModule from './components/eNotice';
// Import Library Module
import LibraryModule from './components/Library';

// Import grades data
import { gradesData } from './Data/gradesData';

// Import mock data
import { mockAttendanceData } from './mockData/attendanceData';
import { mockPaymentData } from './mockData/paymentData';
import { mockNewsData } from './mockData/newsData';
import { mockSchoolEvents } from './mockData/schoolEvents';
import { mockTeachers } from './mockData/teacherData';
import { mockStudents } from './mockData/studentData';

// Import helper functions
import { calculateAttendanceRate, getAttendanceStatus } from './lib/attendanceHelpers';
import { formatCurrency, calculateTotalAmount } from './lib/paymentHelpers';
import { formatDate, getCurrentDate } from './lib/dateHelpers';

// Import A2A services for teacher data
import { a2aTeacherService } from './a2a/services/a2aTeacherService';
import { getTenantId } from './a2a/tenantHelper.js';

// Teacher role normalization
const normalizeTeacherRole = (role) => {
  const userRoles = {
    'teacher': 'teacher',
    'educator': 'teacher',
    '老師': 'teacher',
    'instructor': 'teacher'
  };
  return userRoles[role?.toLowerCase()] || role;
};

// Fetch teacher data after login
const fetchTeacherData = async (user) => {
  const normalizedRole = normalizeTeacherRole(user?.role || user?.user?.role);
  
  if (normalizedRole === 'teacher') {
    try {
      const teacherId = user?.id || user?.user?.id;
      // Get tenant_id using helper (handles multi-tenant format conversion)
      const tenantId = getTenantId(user);
      
      // Fetch teacher's classes
      const classesResult = await a2aTeacherService.getTeacherClasses({
        teacher_id: teacherId,
        tenant_id: tenantId
      });
      
      // Fetch teacher's students
      const studentsResult = await a2aTeacherService.getTeacherStudents({
        teacher_id: teacherId,
        tenant_id: tenantId
      });
      
      // Update user object with teacher data (ensure tenant_id is set)
      const updatedUser = {
        ...user,
        role: 'teacher',
        tenant_id: tenantId, // Ensure tenant_id is always set
        tenantId: tenantId,  // Also set camelCase version for compatibility
        classes: classesResult.success ? classesResult.data : [],
        assigned_classes: classesResult.success ? classesResult.data : [],
        total_students: studentsResult.success ? studentsResult.data.length : 0,
        student_count: studentsResult.success ? studentsResult.data.length : 0,
        // These would be calculated from actual data in a real implementation
        pending_grading: 0,
        pending_attendance: 0
      };
      
      return updatedUser;
    } catch (error) {
      console.error('Error fetching teacher data:', error);
      // Return user with default values if fetch fails
      return {
        ...user,
        role: 'teacher',
        classes: [],
        assigned_classes: [],
        total_students: 0,
        student_count: 0,
        pending_grading: 0,
        pending_attendance: 0
      };
    }
  }
  
  return user;
};

function App() {
  // Main application state
  const [activeTab, setActiveTab] = useState('main');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Sidebar starts hidden by default
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const lastToggleTime = useRef(0); // Track last toggle time to prevent rapid clicking

  // Dummy state for demonstration
  const [attendanceData, setAttendanceData] = useState([]);
  const [paymentData, setPaymentData] = useState([]);
  const [newsData, setNewsData] = useState([]);
  const [schoolEvents, setSchoolEvents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);

  // Initialize data on component mount
  useEffect(() => {
    // Simulate data loading
    setIsLoading(true);
    
    // Load mock data
    setAttendanceData(mockAttendanceData);
    setPaymentData(mockPaymentData);
    setNewsData(mockNewsData);
    setSchoolEvents(mockSchoolEvents);
    setTeachers(mockTeachers);
    setStudents(mockStudents);
    
    // Simulate authentication check
    setTimeout(() => {
      setIsAuthenticated(true);
      setCurrentUser({
        id: 'f6f781a2-dde8-489c-86f9-adbf1f498dde',
        name: 'Student A',
        role: 'student',
        class: 'S1A'
      });
      setIsLoading(false);
    }, 1000);

    // Handle window resize for responsive sidebar behavior
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      // Always hide sidebar on mobile, but don't auto-show on desktop
      if (mobile) {
        setIsSidebarOpen(false);
      }
      // Desktop: keep current sidebar state (don't auto-show)
    };

    // Set initial sidebar state based on screen size
    handleResize();

    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Handle sidebar toggle with proper event handling
  const handleSidebarToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent rapid clicking by checking time between clicks
    const now = Date.now();
    if (now - lastToggleTime.current < 300) { // 300ms debounce
      return;
    }
    lastToggleTime.current = now;
    
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Handle tab changes
  const handleTabChange = (tabName, sourceInfo = null) => {
    console.log('App: Tab change requested:', tabName, 'with source:', sourceInfo);
    
    // Always hide sidebar after navigation (both mobile and desktop)
    setIsSidebarOpen(false);
    
    setActiveTab(tabName);
  };

  // Handle back navigation
  const handleBack = () => {
    // Always hide sidebar when navigating back (both mobile and desktop)
    setIsSidebarOpen(false);
    
    setActiveTab('main');
  };

  // Render main content based on active tab
  const renderMainContent = () => {
    switch (activeTab) {
      case 'main':
        return (
          <MainPage
            currentUser={currentUser}
            attendanceData={attendanceData}
            paymentData={paymentData}
            newsData={newsData}
            schoolEvents={schoolEvents}
            onTabChange={handleTabChange}
            calculateAttendanceRate={calculateAttendanceRate}
            getAttendanceStatus={getAttendanceStatus}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
          />
        );
      
      case 'attendance':
        return (
          <AttendancePage
            attendanceData={attendanceData}
            onBack={handleBack}
            onTabChange={handleTabChange}
            currentUser={currentUser}
            canMarkAttendance={true}  // Teachers can mark
            role="teacher"
            calculateAttendanceRate={calculateAttendanceRate}
            getAttendanceStatus={getAttendanceStatus}
          />
        );
      
      case 'school-event':
        return (
          <SchoolEventPage
            schoolEvents={schoolEvents}
            onBack={handleBack}
            onTabChange={handleTabChange}
            formatDate={formatDate}
          />
        );
      
      case 'teacher-directory':
        return (
          <TeacherDirectoryModule
            teachers={teachers}
            onBack={handleBack}
            onTabChange={handleTabChange}
            onSidebarClose={() => {
              console.log('🔄 App: Closing sidebar from TeacherDirectoryModule');
              setIsSidebarOpen(false);
            }}
          />
        );
      
      case 'grades':
        return (
          <GradesManagementPage
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
            canCreate={true}  // Teachers can create grades
            canEdit={true}    // Teachers can edit
            role="teacher"
          />
        );
      
      case 'payment':
        return (
          <PaymentRecordsModule
            paymentData={paymentData}
            onBack={handleBack}
            onTabChange={handleTabChange}
            formatCurrency={formatCurrency}
            calculateTotalAmount={calculateTotalAmount}
            formatDate={formatDate}
          />
        );
      
      case 'news':
        return (
          <NewsAndNotificationModule
            newsData={newsData}
            onBack={handleBack}
            formatDate={formatDate}
          />
        );
      
      case 'eca':
        return (
          <ECAPage onTabChange={handleTabChange} />
        );
      
      case 'slp-events':
        return (
          <SLPEventPage
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'slp-event-details':
        return (
          <SLPEventDetails
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'slp-event-form':
        return (
          <SLPEventForm
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'slp-student-selector':
        return (
          <SLPStudentSelector
            students={students}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'class-attendance':
        // Redirect to attendance page for teachers
        return (
          <AttendancePage
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
            canMarkAttendance={true}
            role="teacher"
            calculateAttendanceRate={calculateAttendanceRate}
            getAttendanceStatus={getAttendanceStatus}
          />
        );
      
      case 'academic-calendar':
        return (
          <AcademicCalendarModule
            onBack={handleBack}
          />
        );
      
      case 'school-info':
        return (
          <SchoolInfoPage
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'teacher-info':
        return (
          <TeacherInfoPage
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'student-info':
        return (
          <StudentInformationDashboard
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'class-info':
        return (
          <ClassInfoPage
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'staff':
        return (
          <StaffPage
            onBack={handleBack}
          />
        );
      
      case 'group-messages':
        return (
          <GroupMessagesModule currentUser={currentUser} />
        );
      
      case 'sports-days':
        return (
          <SportsDaysModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'timetable':
        return (
          <TimetableModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      // Removed timetable-settings (admin-only, component deleted)
      // Removed timetable-planner (admin-only, removed from navigation)
      
      case 'lunchbox':
        return (
          <LunchboxModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'lesson-plan':
        return (
          <LessonPlanModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'homework':
        return (
          <HomeworkModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
            canCreate={true}  // Teachers can create
            canGrade={true}   // Teachers can grade
            role="teacher"
          />
        );
      
      case 'classes':
        return (
          <ClassManagementModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'students':
        return (
          <StudentManagementModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'slp':
        return (
          <SlpModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'sdr':
        return (
          <SdrModule
            currentUser={currentUser}
            onBack={handleBack}
            onTabChange={handleTabChange}
          />
        );
      
      case 'e-notices':
                return (
                  <ENoticeModule
                    currentUser={currentUser}
                    onBack={handleBack}
                    onTabChange={handleTabChange}
                  />
                );
              case 'library':
                return (
                  <LibraryModule
                    currentUser={currentUser}
                    onBack={handleBack}
                    onTabChange={handleTabChange}
                  />
                );
      
      default:
        return (
          <MainPage
            currentUser={currentUser}
            attendanceData={attendanceData}
            paymentData={paymentData}
            newsData={newsData}
            schoolEvents={schoolEvents}
            onTabChange={handleTabChange}
            calculateAttendanceRate={calculateAttendanceRate}
            getAttendanceStatus={getAttendanceStatus}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
          />
        );
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Loading application...</p>
      </div>
    );
  }

  // Handle login with teacher data fetching
  const handleLogin = async (user) => {
    setIsAuthenticated(true);
    
    // Normalize role
    const normalizedRole = normalizeTeacherRole(user?.role || user?.user?.role);
    const userWithRole = {
      ...user,
      role: normalizedRole,
      // Ensure user object structure is consistent
      user: user.user ? {
        ...user.user,
        role: normalizedRole
      } : undefined
    };
    
    // If teacher, fetch additional data
    if (normalizedRole === 'teacher') {
      try {
        const teacherUser = await fetchTeacherData(userWithRole);
        setCurrentUser(teacherUser);
      } catch (error) {
        console.error('Error fetching teacher data on login:', error);
        setCurrentUser(userWithRole);
      }
    } else {
      setCurrentUser(userWithRole);
    }
  };

  // Show authentication if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="auth-container">
        <ModernLoginForm
          onLogin={handleLogin}
        />
      </div>
    );
  }

  // Main application render
  return (
    <div className="App">
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <button 
            className={`sidebar-toggle ${isSidebarOpen ? 'active' : ''}`}
            onClick={handleSidebarToggle}
            aria-label="Toggle sidebar"
            title={isSidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
          >
            {isSidebarOpen ? '✕' : '☰'}
          </button>
          <h1>MyClassTeacher</h1>
          <div className="user-info">
            <span>Welcome, {currentUser?.name}</span>
            <button 
              className="logout-btn"
              onClick={() => setIsAuthenticated(false)}
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Backdrop */}
      {isSidebarOpen && isMobile && (
        <div 
          className="sidebar-backdrop"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsSidebarOpen(false);
          }}
        />
      )}

      {/* Sidebar Navigation */}
      <div 
        className={`app-sidebar ${isSidebarOpen ? 'open' : 'closed'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <nav className="sidebar-navigation">
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'main' ? 'active' : ''}`}
              onClick={() => handleTabChange('main')}
            >
              🏠 主頁
            </button>
          </div>
          
          {/* Teaching Tools */}
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'classes' ? 'active' : ''}`}
              onClick={() => handleTabChange('classes')}
            >
              📚 我的班級
            </button>
          </div>
          
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'students' ? 'active' : ''}`}
              onClick={() => handleTabChange('students')}
            >
              👨‍🎓 學生管理
            </button>
          </div>
          
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'grades' ? 'active' : ''}`}
              onClick={() => handleTabChange('grades')}
            >
              📈 成績管理
            </button>
          </div>
          
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'homework' ? 'active' : ''}`}
              onClick={() => handleTabChange('homework')}
            >
              📝 家課管理
            </button>
          </div>
          
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'attendance' ? 'active' : ''}`}
              onClick={() => handleTabChange('attendance')}
            >
              📊 考勤管理
            </button>
          </div>
          
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'lesson-plan' ? 'active' : ''}`}
              onClick={() => handleTabChange('lesson-plan')}
            >
              📚 課程計劃
            </button>
          </div>
          
          {/* Schedule */}
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'timetable' ? 'active' : ''}`}
              onClick={() => handleTabChange('timetable')}
            >
              📅 時間表
            </button>
          </div>
          
          {/* Communication */}
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'group-messages' ? 'active' : ''}`}
              onClick={() => handleTabChange('group-messages')}
            >
              💬 小組訊息
            </button>
          </div>
          
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'e-notices' ? 'active' : ''}`}
              onClick={() => handleTabChange('e-notices')}
            >
              📋 電子通告
            </button>
          </div>
          
          {/* Calendar */}
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'academic-calendar' ? 'active' : ''}`}
              onClick={() => handleTabChange('academic-calendar')}
            >
              📅 校曆表
            </button>
          </div>
          
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'school-event' ? 'active' : ''}`}
              onClick={() => handleTabChange('school-event')}
            >
              🎉 學校活動
            </button>
          </div>
          
          {/* Resources */}
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'library' ? 'active' : ''}`}
              onClick={() => handleTabChange('library')}
            >
              📚 圖書館
            </button>
          </div>
          
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'eca' ? 'active' : ''}`}
              onClick={() => handleTabChange('eca')}
            >
              🏃 課外活動
            </button>
          </div>
          
          {/* Information */}
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'teacher-info' ? 'active' : ''}`}
              onClick={() => handleTabChange('teacher-info')}
            >
              👨‍🏫 教師資訊
            </button>
          </div>
          
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'teacher-directory' ? 'active' : ''}`}
              onClick={() => handleTabChange('teacher-directory')}
            >
              👥 教師通訊錄
            </button>
          </div>
          
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'class-info' ? 'active' : ''}`}
              onClick={() => handleTabChange('class-info')}
            >
              🏫 班級資訊
            </button>
          </div>
          
          {/* News */}
          <div className="sidebar-nav-item">
            <button
              className={`sidebar-nav-button ${activeTab === 'news' ? 'active' : ''}`}
              onClick={() => handleTabChange('news')}
            >
              📰 學校宣佈
            </button>
          </div>
        </nav>
      </div>

      {/* Main Content Area */}
      <main className={`app-main ${isSidebarOpen ? 'with-sidebar' : 'without-sidebar'}`}>
        {renderMainContent()}
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <CreditsPanel />
      </footer>
    </div>
  );
}

export default App;
